import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuthState } from './AuthContext';
import { useGlobalWebSocket } from './GlobalWebSocketState';
import type { GlobalWebSocketEvent } from './GlobalWebSocketState';
import { notificationService } from '@/lib/api/notification-service';
import { profileVisitService } from '@/lib/api/profile-visit-service';
import type { Notification } from '@/types/notification';
import { NotificationType } from '@/types/notification';
import type { ProfileVisit } from '@/types/profile-visit';
import { parseProfileVisitSocketData } from '@/lib/api/profile-visit-service';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { subscribeToPushNotifications, getPushSubscription } from '@/lib/service-worker';
import {
  NotificationActionsContext,
  NotificationCountContext,
  NotificationListContext,
  type NotificationActionsContextType,
  type NotificationCountContextType,
  type NotificationListContextType,
} from './notification-context-core';

function asUnknownRecord(value: unknown): Record<string, unknown> | undefined {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : undefined;
}

function isNotificationType(value: unknown): value is Notification['type'] {
  return (
    typeof value === 'string' && Object.values(NotificationType).some((notificationType) => notificationType === value)
  );
}

function parseSocketNotification(value: unknown): Notification {
  const notification = asUnknownRecord(value);
  const parsedData = notification?.data === null ? null : asUnknownRecord(notification?.data);
  if (
    notification === undefined ||
    typeof notification.id !== 'number' ||
    (notification.user_id !== undefined && typeof notification.user_id !== 'number') ||
    !isNotificationType(notification.type) ||
    typeof notification.title !== 'string' ||
    typeof notification.content !== 'string' ||
    typeof notification.is_read !== 'boolean' ||
    (notification.is_pushed !== undefined && typeof notification.is_pushed !== 'boolean') ||
    typeof notification.created_at !== 'string' ||
    (notification.read_at !== undefined && notification.read_at !== null && typeof notification.read_at !== 'string') ||
    (notification.data !== undefined && notification.data !== null && parsedData === undefined)
  ) {
    throw new TypeError('Invalid notification WebSocket payload');
  }

  return {
    id: notification.id,
    type: notification.type,
    title: notification.title,
    content: notification.content,
    is_read: notification.is_read,
    created_at: notification.created_at,
    ...(notification.user_id === undefined ? {} : { user_id: notification.user_id }),
    ...(notification.is_pushed === undefined ? {} : { is_pushed: notification.is_pushed }),
    ...(notification.read_at === undefined ? {} : { read_at: notification.read_at }),
    ...(parsedData === undefined ? {} : { data: parsedData }),
  };
}

export const NotificationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, isAuthenticated } = useAuthState();
  const { subscribe, isSessionJoined, isConnected } = useGlobalWebSocket();
  const isMobile = useMediaQuery('(max-width: 768px)');

  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [profileVisits, setProfileVisits] = useState<ProfileVisit[]>([]);
  const [unseenVisitsCount, setUnseenVisitsCount] = useState(0);
  const [isPushEnabled, setIsPushEnabled] = useState(false);
  const knownNotificationIdsRef = React.useRef<Set<number>>(new Set());
  const knownProfileVisitIdsRef = React.useRef<Set<number>>(new Set());
  const wasConnectedRef = React.useRef(false);

  // Load notifications
  const loadNotifications = useCallback(async () => {
    if (!isAuthenticated) return;

    try {
      setIsLoading(true);
      const data = await notificationService.getNotifications();
      knownNotificationIdsRef.current = new Set(data.notifications.map((item) => item.id));
      setNotifications(data.notifications);
      setUnreadCount(data.unread_count);
    } catch (error) {
      console.error('Failed to load notifications:', error);
    } finally {
      setIsLoading(false);
    }
  }, [isAuthenticated]);

  // Load profile visits
  const loadProfileVisits = useCallback(async () => {
    if (!isAuthenticated) return;

    try {
      const visits = await profileVisitService.getProfileVisits({ include_seen: false });
      knownProfileVisitIdsRef.current = new Set(visits.map((visit) => visit.id));
      setProfileVisits(visits);
      setUnseenVisitsCount(visits.length);
    } catch (error) {
      console.error('Failed to load profile visits:', error);
    }
  }, [isAuthenticated]);

  // Mark notifications as read
  // IMPORTANT: Keep this callback stable by using functional state updates only
  // This prevents the WebSocket effect from re-running on every notification change
  const markAsRead = useCallback(async (notificationIds: number[]) => {
    try {
      const realIds = notificationIds.filter((id) => id > 0);
      if (realIds.length > 0) {
        await notificationService.markAsRead(realIds);
      }

      // Use functional updates to avoid dependency on notifications state
      setNotifications((prev) => {
        const idsSet = new Set(notificationIds);
        let unreadDecrement = 0;

        const updated = prev.map((n) => {
          if (idsSet.has(n.id)) {
            if (!n.is_read) unreadDecrement++;
            return { ...n, is_read: true, read_at: new Date().toISOString() };
          }
          return n;
        });

        // Schedule unread count update after this render
        if (unreadDecrement > 0) {
          setTimeout(() => {
            setUnreadCount((c) => Math.max(0, c - unreadDecrement));
          }, 0);
        }

        return updated;
      });
    } catch (error) {
      console.error('Failed to mark notifications as read:', error);
    }
  }, []); // No dependencies - fully stable callback

  // Mark all notifications as read
  const markAllAsRead = useCallback(async () => {
    try {
      await notificationService.markAllAsRead();

      setNotifications((prev) => prev.map((n) => ({ ...n, is_read: true, read_at: new Date().toISOString() })));

      setUnreadCount(0);
    } catch (error) {
      console.error('Failed to mark all notifications as read:', error);
    }
  }, []);

  // Delete notification
  // IMPORTANT: Keep this callback stable by using functional state updates only
  const deleteNotification = useCallback(async (notificationId: number) => {
    try {
      if (notificationId > 0) {
        await notificationService.deleteNotification(notificationId);
      }
      knownNotificationIdsRef.current.delete(notificationId);

      // Use functional update to check if notification was unread
      setNotifications((prev) => {
        const notification = prev.find((n) => n.id === notificationId);
        const wasUnread = notification && !notification.is_read;

        // Schedule unread count update if needed
        if (wasUnread) {
          setTimeout(() => {
            setUnreadCount((c) => Math.max(0, c - 1));
          }, 0);
        }

        return prev.filter((n) => n.id !== notificationId);
      });
    } catch (error) {
      console.error('Failed to delete notification:', error);
    }
  }, []); // No dependencies - fully stable callback

  // Mark profile visits as seen
  const markVisitsAsSeen = useCallback(async (visitIds: number[]) => {
    try {
      await profileVisitService.markAsSeen(visitIds);

      setProfileVisits((prev) => prev.filter((v) => !visitIds.includes(v.id)));
      setUnseenVisitsCount((prev) => Math.max(0, prev - visitIds.length));
    } catch (error) {
      console.error('Failed to mark visits as seen:', error);
    }
  }, []);

  // Request push notification permission and subscribe
  const requestPushPermission = useCallback(async () => {
    if (!('Notification' in window)) {
      console.warn('Notifications API not supported in this browser');
      return;
    }

    const toastModule = await import('@/components/notifications/NotificationToast');
    const showToast = isMobile ? toastModule.showMobileNotificationToast : toastModule.showNotificationToast;

    // Bereits blockiert? User muss manuell in Browser-Einstellungen aendern
    if (Notification.permission === 'denied') {
      showToast({
        type: NotificationType.SYSTEM,
        title: 'Benachrichtigungen blockiert',
        content: 'Bitte aktiviere Benachrichtigungen in deinen Browser-Einstellungen',
      });
      return;
    }

    try {
      const permission = await Notification.requestPermission();

      if (permission === 'granted') {
        setIsPushEnabled(true);

        // Web Push Subscription beim Server registrieren
        try {
          const subscription = await subscribeToPushNotifications();

          if (subscription) {
            showToast({
              type: NotificationType.SYSTEM,
              title: 'Push-Benachrichtigungen aktiviert ✓',
              content: 'Du erhältst jetzt Benachrichtigungen auch wenn der Browser geschlossen ist',
            });
          } else {
            // Fallback: Nur In-App Notifications
            showToast({
              type: NotificationType.SYSTEM,
              title: 'Benachrichtigungen aktiviert',
              content: 'Du erhältst Browser-Benachrichtigungen wenn die App geöffnet ist',
            });
          }
        } catch (subError) {
          console.warn('Push subscription failed, falling back to in-app only:', subError);
          showToast({
            type: NotificationType.SYSTEM,
            title: 'Benachrichtigungen aktiviert',
            content: 'Du erhältst Browser-Benachrichtigungen wenn die App geöffnet ist',
          });
        }

        // Test-Notification um zu zeigen dass es funktioniert
        setTimeout(() => {
          try {
            const testNotification = new Notification('Test erfolgreich! 🎉', {
              body: 'Browser-Benachrichtigungen funktionieren.',
              icon: '/favicon.svg',
              tag: 'test-notification',
            });
            setTimeout(() => {
              testNotification.close();
            }, 4000);
          } catch (e) {
            console.warn('Test notification failed:', e);
          }
        }, 500);
      } else if (permission === 'denied') {
        setIsPushEnabled(false);
        showToast({
          type: NotificationType.SYSTEM,
          title: 'Benachrichtigungen abgelehnt',
          content: 'Du kannst dies später in den Browser-Einstellungen ändern',
        });
      }
    } catch (error) {
      console.error('Failed to request notification permission:', error);
    }
  }, [isMobile]);

  // Check push permission status and sync subscription
  useEffect(() => {
    if ('Notification' in window) {
      const isGranted = Notification.permission === 'granted';
      setIsPushEnabled(isGranted);

      // Wenn Permission erteilt und User eingeloggt, Subscription synchronisieren
      if (isGranted && isAuthenticated) {
        void (async () => {
          try {
            // Prüfe ob bereits subscribed
            const existingSubscription = await getPushSubscription();
            if (!existingSubscription) {
              // Automatisch subscriben wenn Permission schon erteilt
              await subscribeToPushNotifications();
            }
          } catch (error) {
            console.warn('Failed to synchronize the push subscription:', error);
          }
        })();
      }
    }
  }, [isAuthenticated]);

  // Load initial data
  useEffect(() => {
    if (isAuthenticated) {
      void loadNotifications();
      void loadProfileVisits();
    }
  }, [isAuthenticated, loadNotifications, loadProfileVisits]);

  useEffect(() => {
    const reconnected = isConnected && !wasConnectedRef.current;
    wasConnectedRef.current = isConnected;
    if (isAuthenticated && reconnected) {
      void Promise.all([loadNotifications(), loadProfileVisits()]);
    }
  }, [isAuthenticated, isConnected, loadNotifications, loadProfileVisits]);

  // WebSocket listener for real-time notifications
  useEffect(() => {
    if (!isAuthenticated || !user) return;

    const handleWebSocketMessage = (data: GlobalWebSocketEvent): void => {
      try {
        if (data.type === 'notification') {
          const notification = parseSocketNotification(data.notification);

          if (knownNotificationIdsRef.current.has(notification.id)) return;
          knownNotificationIdsRef.current.add(notification.id);
          setNotifications((prev) => [notification, ...prev]);
          setUnreadCount((count) => count + (notification.is_read ? 0 : 1));

          void import('@/components/notifications/NotificationToast').then((toastModule) => {
            const showToast = isMobile ? toastModule.showMobileNotificationToast : toastModule.showNotificationToast;
            const notificationData: unknown = notification.data;
            const toastData = asUnknownRecord(notificationData);
            const characterImage = toastData?.character_image;
            showToast({
              type: notification.type,
              title: notification.title,
              content: notification.content,
              ...(toastData === undefined ? {} : { data: toastData }),
              ...(typeof characterImage === 'string' ? { characterImage } : {}),
              notificationId: notification.id,
            });
          });
        } else if (data.type === 'notification_count_update') {
          if (typeof data.unread_count !== 'number') {
            throw new TypeError('Invalid notification count WebSocket payload');
          }
          setUnreadCount(data.unread_count);
        } else if (data.type === 'unread_count_update') {
          // Fallback: Bei session-spezifischer Änderung (Chat gelesen) den globalen Zähler frisch laden
          void (async () => {
            try {
              const { unread_count } = await notificationService.getUnreadCount();
              setUnreadCount(unread_count);
            } catch (error) {
              console.error('Failed to refresh the unread notification count:', error);
            }
          })();
        } else if (
          (data.type === 'message' || data.type === 'chat_message') &&
          data.is_from_character === true &&
          data.session_id
        ) {
          // Smart Chat Suppression: Use isSessionJoined from GlobalWebSocketContext as Single Source of Truth
          // This is more reliable than URL-based checks which can have race conditions
          const isUserViewingSession = isSessionJoined(data.session_id) && document.visibilityState === 'visible';

          // If user has joined this session and tab is visible, suppress notification
          // The user will see the message directly in the chat
          if (isUserViewingSession) {
            // Don't show toast or add to notification list - user sees it in real-time
            return;
          }

          // NOTE: We do NOT show a toast here anymore!
          // The backend sends the notification separately via type: 'notification'
          // which already shows the toast. Showing one here would cause DUPLICATE toasts.
          // The notification handler (above) handles toast display.
        } else if (data.type === 'profile_visit') {
          // Handle profile visit - only update ProfileVisits list
          // The actual notification arrives separately as type: 'notification' from the backend
          const newVisit = parseProfileVisitSocketData(data.data);

          if (knownProfileVisitIdsRef.current.has(newVisit.id)) return;
          knownProfileVisitIdsRef.current.add(newVisit.id);
          setProfileVisits((prev) => [newVisit, ...prev]);
          setUnseenVisitsCount((prev) => prev + 1);

          // NOTE: We do NOT show a toast here anymore!
          // The backend sends the notification separately via type: 'notification'
          // which already shows the toast. This prevents DUPLICATE toasts.
        }
      } catch (error) {
        console.error('Failed to parse WebSocket message:', error);
      }
    };

    // Subscribe to WebSocket events using GlobalWebSocketContext
    const unsubscribe = subscribe(handleWebSocketMessage);

    return () => {
      unsubscribe();
    };
  }, [isAuthenticated, user, isMobile, subscribe, isSessionJoined]);

  // Memoize count value - only for badges (changes when count changes)
  const countValue = useMemo<NotificationCountContextType>(
    () => ({
      unreadCount,
      unseenVisitsCount,
    }),
    [unreadCount, unseenVisitsCount]
  );

  // Memoize list value - for notification panel (changes when list changes)
  const listValue = useMemo<NotificationListContextType>(
    () => ({
      notifications,
      profileVisits,
      isLoading,
    }),
    [notifications, profileVisits, isLoading]
  );

  // Memoize actions value - stable references via useCallback
  const actionsValue = useMemo<NotificationActionsContextType>(
    () => ({
      loadNotifications,
      markAsRead,
      markAllAsRead,
      deleteNotification,
      loadProfileVisits,
      markVisitsAsSeen,
      requestPushPermission,
      isPushEnabled,
    }),
    [
      loadNotifications,
      markAsRead,
      markAllAsRead,
      deleteNotification,
      loadProfileVisits,
      markVisitsAsSeen,
      requestPushPermission,
      isPushEnabled,
    ]
  );

  return (
    <NotificationCountContext.Provider value={countValue}>
      <NotificationListContext.Provider value={listValue}>
        <NotificationActionsContext.Provider value={actionsValue}>{children}</NotificationActionsContext.Provider>
      </NotificationListContext.Provider>
    </NotificationCountContext.Provider>
  );
};
